"use client";

import type { RefObject } from "react";
import {
  DiagramFrame,
  Label,
  LegendRow,
  SHAPE,
} from "@/components/tools/geometry/diagram";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

/*
 * An angle on the unit circle: the terminal side, the point (cos θ, sin θ), and the
 * three lengths that give the functions — cosine along the x-axis, sine up from it,
 * tangent along the line x = 1.
 */

export interface TrigDiagramProps {
  /** The angle brought into [0°, 360°). */
  degrees: number;
  title: string;
  svgRef: RefObject<SVGSVGElement | null>;
  /** "cos θ = −√3/2" and the like, shown beside the figure. */
  labels: { cos: string; sin: string; tan: string };
}

const CX = 108;
const CY = 120;
const R = 84;
const LEGEND_X = 214;
const TAN_COLOR = CHART_COLORS[2];
const TAN_LIMIT = 1.8;

const radians = (degrees: number) => (degrees * Math.PI) / 180;

function sample(color: string, dashed = false) {
  return (
    <line
      stroke={color}
      strokeDasharray={dashed ? "5 4" : undefined}
      strokeWidth={3}
      x1={0}
      x2={20}
      y1={0}
      y2={0}
    />
  );
}

export function TrigDiagram({
  degrees,
  title,
  svgRef,
  labels,
}: TrigDiagramProps) {
  const angle = radians(degrees);
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const p = { x: CX + R * cos, y: CY - R * sin };
  const tan = Math.abs(cos) > 1e-9 ? sin / cos : Number.NaN;
  const showTan =
    Number.isFinite(tan) && cos > 0.2 && Math.abs(tan) <= TAN_LIMIT;
  const arcRadius = 24;
  const arcEnd = {
    x: CX + arcRadius * cos,
    y: CY - arcRadius * sin,
  };
  const large = degrees > 180 ? 1 : 0;
  const mid = angle / 2;
  return (
    <DiagramFrame svgRef={svgRef} title={title}>
      {[
        ["I", 1, -1],
        ["II", -1, -1],
        ["III", -1, 1],
        ["IV", 1, 1],
      ].map(([text, sx, sy]) => (
        <Label
          anchor="middle"
          color="#c8c8c8"
          key={String(text)}
          size={20}
          x={CX + Number(sx) * (R + 6) * 0.78}
          y={CY + Number(sy) * (R + 6) * 0.78 + 7}
        >
          {String(text)}
        </Label>
      ))}
      <line
        stroke={SHAPE.muted}
        strokeWidth={1.2}
        x1={CX - R - 14}
        x2={CX + R + 14}
        y1={CY}
        y2={CY}
      />
      <line
        stroke={SHAPE.muted}
        strokeWidth={1.2}
        x1={CX}
        x2={CX}
        y1={CY - R - 14}
        y2={CY + R + 14}
      />
      <circle
        cx={CX}
        cy={CY}
        fill="none"
        r={R}
        stroke={SHAPE.ink}
        strokeWidth={2}
      />
      {showTan ? (
        <line
          stroke={TAN_COLOR}
          strokeWidth={3}
          x1={CX + R}
          x2={CX + R}
          y1={CY}
          y2={CY - tan * R}
        />
      ) : null}
      <line
        stroke={SHAPE.muted}
        strokeWidth={1.5}
        x1={CX}
        x2={showTan ? CX + R : p.x}
        y1={CY}
        y2={showTan ? CY - tan * R : p.y}
      />
      <line
        stroke={CHART_COLORS[0]}
        strokeWidth={4}
        x1={CX}
        x2={p.x}
        y1={CY}
        y2={CY}
      />
      <line
        stroke={SHAPE.guide}
        strokeDasharray="5 4"
        strokeWidth={3}
        x1={p.x}
        x2={p.x}
        y1={CY}
        y2={p.y}
      />
      <line
        stroke={SHAPE.accent}
        strokeWidth={2.5}
        x1={CX}
        x2={p.x}
        y1={CY}
        y2={p.y}
      />
      {degrees > 0.5 ? (
        <path
          d={`M${CX + arcRadius} ${CY} A${arcRadius} ${arcRadius} 0 ${large} 0 ${arcEnd.x} ${arcEnd.y}`}
          fill="none"
          stroke={SHAPE.accent}
          strokeWidth={2}
        />
      ) : null}
      <circle
        cx={p.x}
        cy={p.y}
        fill={SHAPE.accent}
        r={5}
        stroke="#fff"
        strokeWidth={1.5}
      />
      <Label
        anchor="middle"
        color={SHAPE.ink}
        italic
        x={CX + (arcRadius + 11) * Math.cos(mid)}
        y={CY - (arcRadius + 11) * Math.sin(mid) + 4}
      >
        θ
      </Label>
      <LegendRow
        sample={sample(CHART_COLORS[0])}
        text={labels.cos}
        x={LEGEND_X}
        y={84}
      />
      <LegendRow
        sample={sample(SHAPE.guide, true)}
        text={labels.sin}
        x={LEGEND_X}
        y={116}
      />
      <LegendRow
        sample={sample(TAN_COLOR)}
        text={labels.tan}
        x={LEGEND_X}
        y={148}
      />
    </DiagramFrame>
  );
}
